import React, { memo, useCallback, useMemo, useRef, useState } from 'react';

import {
  CellPosition,
  CellValueChangedEvent,
  DetailGridInfo,
  GetRowIdParams,
  GridReadyEvent,
  IRowNode,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { Button } from 'primereact/button';

import { WithLocalStorage } from '@/components/hoc';
import { useDefaultGridOptions } from '@/grid';
import { defaultColDef } from '@/grid/defaults';
import { useContextMenu } from '@/utils/context-menu';

import { useGridSelectionRestore } from '../hooks/useGridSelectionRestore';
import { freezeEmbryoColDefs } from '../utils/columnDefs';
import { SelectionService } from '../utils/selectionService';
import { LiveEmbryoGridProps } from '../utils/types';
import { FreezeSubGridInfo } from './FreezeSubGridInfo';

export const LiveEmbryoGrid: React.FC<LiveEmbryoGridProps> = memo(
  ({
    liveEmbryos,
    tempEmbryos,
    gridRef,
    onEdit,
    handleUpdateEmbryo,
    handleLiveEmbryoSelectionChanged,
    isSlaughterHouse,
    mode,
    isExport,
    isUpdateRunning,
    node,
    api,
    isExpanded,
    rowData,
  }) => {
    const rowId = node.id!;
    const defaultGridOptions = useDefaultGridOptions();
    const { getContextMenuItems } = useContextMenu({ idKey: 'id' });
    const [isLocked, setIsLocked] = useState(true);

    // Stable context ref so ag-grid doesn't reset focus on re-renders
    const stableContextRef = useRef<Record<string, unknown> | null>(null);
    if (!stableContextRef.current) {
      stableContextRef.current = {};
    }
    Object.assign(stableContextRef.current, {
      isExport,
      isUpdateRunning,
      partnershipId: rowData?.partnershipId,
      billAccountCaningOption: rowData?.billAccountCaningOption,
    });

    const stableGridOptions = useMemo(
      () => ({
        ...defaultGridOptions,
        domLayout: isExpanded ? ('autoHeight' as const) : ('normal' as const),
        suppressColumnVirtualisation: false,
        suppressRowVirtualisation: false,
        enableCellTextSelection: false,
        suppressDragLeaveHidesColumns: true,
        suppressBrowserResizeObserver: true,
        suppressAnimationFrame: true,
        suppressScrollOnNewData: true,
        suppressModelUpdateAfterUpdateTransaction: true,
        suppressAggFuncInHeader: true,
        suppressAggAtRootLevel: true,
        suppressRowTransform: true,
        suppressColumnMoveAnimation: true,
        suppressRowHoverHighlight: true,
        suppressRowDeselection: true,
        suppressRowDrag: true,
        rowBuffer: 20,
        cacheBlockSize: 50,
        maxBlocksInCache: 10,
        animateRows: false,
      }),
      [defaultGridOptions, isExpanded],
    );

    const { wrappedSelectionChanged, onModelUpdated } = useGridSelectionRestore(
      {
        embryos: liveEmbryos,
        getSelectedIds: (selection) => selection.liveEmbryoIds,
        getNodeId: (node) => node.data.id,
        onSelectionChanged: handleLiveEmbryoSelectionChanged,
      },
    );

    const toggleLock = () => {
      const newLockState = !isLocked;
      setIsLocked(newLockState);

      if (gridRef.current?.api) {
        const columns = gridRef.current.api.getColumns();
        const updatedColDefs =
          columns?.map((col) => {
            const colDef = col.getColDef();
            const toggleableFields = [
              'strawNumber',
              'stage',
              'grade',
              'tt',
              'd65',
              'd7',
              'dtPlus',
              'crackedZona',
              'lowQuality',
            ];
            const isToggleableField = toggleableFields.includes(
              colDef.field as string,
            );

            return {
              ...colDef,
              editable: isToggleableField ? !newLockState : colDef.editable,
            };
          }) || [];
        gridRef.current.api.setGridOption('columnDefs', updatedColDefs);
      }
    };

    const handleCellValueChanged = useCallback(
      (params: CellValueChangedEvent) => {
        handleUpdateEmbryo(params);
        onEdit(params.data.fertilizationId);
      },
      [handleUpdateEmbryo, onEdit],
    );

    const handleGridReady = useCallback(
      (params: GridReadyEvent) => {
        const gridInfo: DetailGridInfo = {
          id: rowId,
          api: params.api,
        };

        api.addDetailGridInfo(rowId, gridInfo);

        const selection = SelectionService.getSelection();
        params.api.forEachNode((node: IRowNode) => {
          const isSelected = selection.liveEmbryoIds.includes(node.data.id);
          node.setSelected(isSelected);
        });
      },
      [api, rowId],
    );

    const handleCellKeyDown = (event: any) => {
      if (event.event.key === 'Enter') {
        const currentCell: CellPosition = event.api.getFocusedCell();
        if (currentCell) {
          const nextRowIndex = currentCell.rowIndex + 1;
          event.api.setFocusedCell(nextRowIndex, currentCell.column);
          event.api.clearRangeSelection();
        }
      }
    };

    return (
      <div className="flex flex-row gap-5">
        {isSlaughterHouse && (
          <FreezeSubGridInfo
            liveEmbryos={liveEmbryos}
            tempEmbryos={tempEmbryos}
          />
        )}
        <div className="w-full">
          <div className="flex items-center justify-between px-4">
            <h3>Live Embryos</h3>
            <Button
              icon={isLocked ? 'pi pi-lock' : 'pi pi-lock-open'}
              onClick={toggleLock}
              className="p-button-text"
              tooltip={isLocked ? 'Unlock Grid' : 'Lock Grid'}
            />
          </div>
          <div
            className={`ag-theme-transova${mode === 'dark' ? '-dark' : ''} w-full ${isExpanded ? '' : 'h-[27rem] overflow-y-auto'}`}
          >
            <WithLocalStorage storageKey="freeze-live-embryo-grid">
              <AgGridReact
                className="h-full w-full"
                ref={gridRef}
                rowData={liveEmbryos}
                defaultColDef={{
                  ...defaultColDef,
                  editable: !isLocked,
                }}
                getRowId={(params: GetRowIdParams) => params.data.id}
                getContextMenuItems={getContextMenuItems}
                columnDefs={freezeEmbryoColDefs}
                masterDetail={true}
                onCellValueChanged={handleCellValueChanged}
                onCellKeyDown={handleCellKeyDown}
                onSelectionChanged={wrappedSelectionChanged}
                onModelUpdated={onModelUpdated}
                suppressRowClickSelection={true}
                rowSelection="multiple"
                gridOptions={stableGridOptions}
                context={stableContextRef.current}
                onGridReady={handleGridReady}
              />
            </WithLocalStorage>
          </div>
        </div>
      </div>
    );
  },
);

LiveEmbryoGrid.displayName = 'LiveEmbryoGrid';
